// __TRAY_REMOTES__ is the canonical webpack-baked manifest global, named
// after the same dunder convention as __webpack_require__ etc. The
// no-underscore-dangle rule does not understand the convention.
/* eslint-disable no-underscore-dangle */
import federatedDomains from './federated-domain-paths.json';

const buildFederatedAppDomainURL = (appPath: string, baseUrl: string, basePath?: string) => {
    if (basePath) {
        return new URL(`${basePath}/${appPath}`, baseUrl);
    }
    return new URL(appPath, baseUrl);
};

/**
 * Defensive read of `window.__TRAY_REMOTES__`. Returns a plain object or
 * `undefined`; anything else (null, array, throwing Proxy, primitives)
 * degrades to undefined so the caller falls back to legacy resolution.
 */
const readManifest = (): Record<string, unknown> | undefined => {
    try {
        // Inline cast (instead of leaning on global.d.ts) so consumer apps
        // that run their own fork-ts-checker still type-check this file in
        // isolation.
        const value = (window as { __TRAY_REMOTES__?: unknown }).__TRAY_REMOTES__;
        if (
            value &&
            typeof value === 'object' &&
            !Array.isArray(value) &&
            Object.prototype.toString.call(value) === '[object Object]'
        ) {
            return value as Record<string, unknown>;
        }
    } catch {
        // window.__TRAY_REMOTES__ may be a hostile getter or throwing Proxy;
        // treat any failure as "no manifest" and fall back.
    }
    return undefined;
};

/**
 * Use in app entries to set the federated domains on the window object.
 * This allows the federated apps remoteEntry to be loaded from the correct path.
 * Note: it should be called before the app bootstrap is loaded.
 *
 * Resolution order for each domain key:
 *   1. window.__TRAY_REMOTES__[key] (versioned manifest inlined by the host's
 *      index.html during atomic-pointer deploys). The manifest value is a full
 *      origin-relative path, so TRAY_BASE_PATH is not re-applied on top.
 *   2. federated-domain-paths.json[key] combined with TRAY_BASE_PATH (legacy
 *      unversioned resolution, used for local dev, prbuilds, and any app that
 *      has not yet opted in to versioned deploys).
 */
export const setFederatedDomainsOnWindow = () => {
    const baseUrl = window.location.origin;

    const { TRAY_BASE_PATH = '' } = window.TRAY_CONFIG || {};
    const manifest = readManifest();

    Object.entries(federatedDomains).forEach(([key, value]: [string, string]) => {
        const manifestPath = manifest?.[key];
        if (typeof manifestPath === 'string' && manifestPath.length > 0) {
            window[key] = new URL(manifestPath, baseUrl);
            return;
        }
        window[key] = buildFederatedAppDomainURL(value, baseUrl, `${TRAY_BASE_PATH}`);
    });
};
